if (!customElements.get("recently-viewed-products")) {
  customElements.define(
    "recently-viewed-products",
    /**
     * @class RecentlyViewedProducts
     * @extends {HTMLElement}
     * A custom element to display Recently Viewed Products.
     * It fetches product handles from localStorage, retrieves their content via the Shopify Search API,
     * and renders the result. If no products have been viewed, the entire section is hidden.
     * 
     * Important: This should only initialize if consent has been granted
     */
    class RecentlyViewedProducts extends HTMLElement {
      constructor() {
        super();

        // We hide the Shopify Section until we know we can display it
        this.hideSection();
      }

      /**
       * Initializes the component by fetching and rendering recently viewed products.
       * @async
       */
      async init() {
        const productData = this.getDataFromLocalstorage();

        if (productData.length === 0) {
          return;
        }

        // The product data is stored as "handle::variantId". We only need the handle.
        const productHandles = productData
          .filter(this.filterCurrentProduct.bind(this))
          .reverse()
          .map((product) => product.split("::")[0]);
        
        const productContent = await this.fetchProductContent(productHandles);
        this.showSection();
        this.appendContent(productContent);
      }

      /**
       * Fetches the HTML content for a list of products using the Shopify Search API.
       * @async
       * @param {string[]} productHandles - An array of product handles to fetch.
       * @returns {Promise<string>} A promise that resolves with the HTML content as a string.
       */
      async fetchProductContent(productHandles) {
        try {
          const query = this.createQuery(productHandles, this.dataset.configuration);
          const baseUrl = `https://${location.host}/${window.routes.search_url}`;
          const url = new URL(baseUrl);
          
          // Use different template based on data-view attribute
          const view = this.dataset.view || "recently-viewed-products";
          url.searchParams.append("view", view);
          url.searchParams.append("q", query);
          url.searchParams.append("config", this.dataset.configuration);

          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const htmlContent = await response.text();

          return htmlContent;
        } catch (error) {
          console.error('Error fetching recently viewed products:', error);
          return '';
        }
      }

      /**
       * Creates a search query string for the Shopify Search API.
       * @param {string[]} productHandles - An array of product handles.
       * @param {string} config - The configuration string to be included in the query.
       * @returns {string} The formatted search query string (e.g., "handle:product-a OR handle:product-b OR config:123").
       */
      createQuery(productHandles, config) {
        const query = productHandles
          .map((handle) => `handle:${handle}`)
          .concat([`config:${config}`])
          .join(" OR ");

        return query;
      }

      /**
       * Injects the fetched HTML content into the component's inner HTML.
       * @param {string} content - The HTML string to be rendered inside the component.
       */
      appendContent(content) {
        this.innerHTML = content;
        
        // Initialize slider components after content is loaded with a small delay
        setTimeout(() => {
          this.initializeSliders();
        }, 100);
      }

      initializeSliders() {
        // Find and initialize any slider components
        const sliders = this.querySelectorAll('slider-component');
        sliders.forEach(slider => {
          if (slider && typeof slider.update === 'function') {
            slider.update();
          }
        });
      }

      /**
       * Retrieves the list of recently viewed products from localStorage.
       * @returns {string[]} An array of product identifiers, in "handle::variantId" format.
       */
      getDataFromLocalstorage() {
        const STORAGE_KEY = "RecentlyViewedProducts";
        const productsFromStorage = localStorage.getItem(STORAGE_KEY);
        const viewedProducts = productsFromStorage
          ? JSON.parse(productsFromStorage)
          : [];

        return viewedProducts;
      }

      /**
       * Hides the parent Shopify section element by adding a "hidden" class.
       * This is triggered when there are no recently viewed products to display.
       */
      hideSection() {
        const mainSection = document.getElementById(`shopify-section-${this.dataset.mainSectionId}`);
        if (mainSection) {
          mainSection.classList.add("hidden");
        }
      }

      /**
       * Shows the parent Shopify section element by removing the "hidden" class.
       */
      showSection() {
        const mainSection = document.getElementById(`shopify-section-${this.dataset.mainSectionId}`);
        if (mainSection) {
          mainSection.classList.remove("hidden");
        }
      }

      /**
       * Removes the Currently Viewed product from the list. Useful
       * when viewing the Recently Viewed Products section in the Product
       * Page to avoid showing the same product
       * 
       * @returns {boolean}
       */
      filterCurrentProduct(productKey) {
        if (this.dataset.currentProductHandle) {
          const productHandle = productKey.split(':')[0];
          return productHandle !== this.dataset.currentProductHandle;
        }

        return true;
      }
    }
  );
}

/**
 * Since the Recently Viewed Products section should only show when a Customer
 * has given consent, then we need to check with the Consent Tracking API first.
 */
document.addEventListener('DOMContentLoaded', () => {
  const recentlyViewedElements = [...document.querySelectorAll('recently-viewed-products')];
  if (recentlyViewedElements.length === 0) return;

  window.Shopify.loadFeatures(
    [
      {
        name: 'consent-tracking-api',
        version: '0.1',
      },
    ],
    (error) => {
      if (error) {
        console.error("Failed to load Shopify's Customer Privacy API:", error);
        return;
      }

      let isInitialized = false;

      const initializeComponent = () => {
        if (isInitialized) return;
        
        recentlyViewedElements.forEach(elem => elem.init()); 
        isInitialized = true;
      };

      const handleConsent = (consent) => {
        if (consent === 'yes') {
          initializeComponent();
        }
      };

      document.addEventListener('shopify:consent:changed', (event) => {
        handleConsent(event.detail.currentConsent);
      });

      (async () => {
        try {
          const consent = await window.Shopify.customerPrivacy.getTrackingConsent();
          handleConsent(consent);
        } catch (getConsentError) {
          console.error("Could not get tracking consent on load:", getConsentError);
        }
      })();
    }
  );
});